import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCheckIn,
  adminDownloadCard,
  adminDownloadGeneralCards,
  adminMakeReviewer,
  adminPreviewCard,
  adminRegistrations,
  adminRemoveReviewer,
  adminResendRegistration,
  fetchSettings,
  saveBlob,
  type ConferenceRegistration,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";
import { PaginationBar, useClientPagination } from "../shells/pagination";
import { filterRegistrations } from "./registrationSearch";

function cardTypeLabel(row: ConferenceRegistration, t: ReturnType<typeof useLanguage>["t"]) {
  if (row.card_type === "hotel" || row.has_hotel_reservation) return t.admin.cardTypeHotel;
  return t.admin.cardTypeParticipant;
}

function BadgePreviewDialog({
  url,
  aspectRatio,
  title,
  printHint,
  closeLabel,
  onClose,
}: {
  url: string | null;
  aspectRatio: number;
  title: string;
  printHint: string;
  closeLabel: string;
  onClose: () => void;
}) {
  if (!url) return null;
  return (
    <div className="dn-badge-preview-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="dn-badge-preview-dialog">
        <div className="dn-badge-preview-toolbar">
          <strong>{title}</strong>
          <button type="button" className="dn-btn dn-btn-ghost" onClick={onClose}>
            {closeLabel}
          </button>
        </div>
        <p className="dn-muted" style={{ margin: "0 0 0.75rem" }}>
          {printHint}
        </p>
        <div className="dn-badge-preview-stage">
          <img
            src={url}
            alt={title}
            className="dn-badge-preview-img"
            style={{ aspectRatio: String(aspectRatio) }}
          />
        </div>
      </div>
      <button type="button" className="dn-badge-preview-backdrop" aria-label={closeLabel} onClick={onClose} />
    </div>
  );
}

function RowActions({
  row,
  onReload,
  onPreview,
}: {
  row: ConferenceRegistration;
  onReload: () => Promise<void>;
  onPreview: (blob: Blob) => void;
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const kind = row.card_type === "hotel" || row.has_hotel_reservation ? "hotel" : "participant";

  return (
    <div className="dn-actions" ref={rootRef}>
      <button
        type="button"
        className="dn-btn dn-btn-ghost dn-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        {t.admin.actions} ▾
      </button>
      {open && (
        <div className="dn-actions-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const blob = await adminPreviewCard(row.id);
                onPreview(blob);
              })
            }
          >
            {t.admin.previewCard}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const blob = await adminDownloadCard(row.id, false, "pdf");
                saveBlob(blob, `card-${kind}-${row.registration_code}.pdf`);
              })
            }
          >
            {t.admin.printDownloadCard}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const blob = await adminDownloadCard(row.id, false, "png");
                saveBlob(blob, `card-${kind}-${row.registration_code}.png`);
              })
            }
          >
            {t.admin.downloadCardPng}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await adminCheckIn(row.id);
                await onReload();
              })
            }
          >
            {t.admin.checkIn}
          </button>
          {row.is_reviewer ? (
            <button
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await adminRemoveReviewer(row.id);
                  await onReload();
                })
              }
            >
              {t.admin.removeReviewer}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await adminMakeReviewer(row.id);
                  await onReload();
                })
              }
            >
              {t.admin.makeReviewer}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await adminResendRegistration(row.id);
              })
            }
          >
            {t.admin.resendRegistration}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              navigate(`/admin/registrations/${row.id}/hotel-reservation`);
            }}
          >
            {t.admin.sendHotelReservation}
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminRegistrationsPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferenceRegistration[]>([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [genericCopies, setGenericCopies] = useState(10);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [badgeAspect, setBadgeAspect] = useState(80 / 120);

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        const w = Number(s.card_width_mm || 80);
        const h = Number(s.card_height_mm || 120);
        if (w > 0 && h > 0) setBadgeAspect(w / h);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const showPreview = (blob: Blob) => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
  };

  const closePreview = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const reload = () => adminRegistrations().then(setRows);
  useEffect(() => {
    reload().catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => filterRegistrations(rows, q), [rows, q]);
  const pager = useClientPagination(filtered, 25);

  const printSelectedCards = async () => {
    const ids = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const chosen = rows.filter((r) => ids.includes(r.id));
    if (!chosen.length) return;
    setBusy(true);
    try {
      for (const r of chosen) {
        const blob = await adminDownloadCard(r.id);
        const kind = r.card_type === "hotel" || r.has_hotel_reservation ? "hotel" : "participant";
        saveBlob(blob, `card-${kind}-${r.registration_code}.pdf`);
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadGeneric = async (preview: boolean) => {
    setBusy(true);
    try {
      const format = preview ? "png" : "pdf";
      const blob = await adminDownloadGeneralCards(genericCopies, preview, format);
      if (preview) {
        showPreview(blob);
      } else {
        saveBlob(blob, `general-participation-cards-x${genericCopies}.pdf`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={t.admin.registrations} backTo="/admin" />

      <BadgePreviewDialog
        url={previewUrl}
        aspectRatio={badgeAspect}
        title={t.admin.badgePreviewTitle}
        printHint={t.admin.cardPrintHelp}
        closeLabel={t.admin.closePreview}
        onClose={closePreview}
      />

      <div className="dn-card" style={{ marginBottom: "1.25rem", maxWidth: 720 }}>
        <h3>{t.admin.generalCardsTitle}</h3>
        <p className="dn-muted">{t.admin.generalCardsHelp}</p>
        <p className="dn-muted">{t.admin.cardPrintHelp}</p>
        <div className="dn-cta-row" style={{ alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <label>
            {t.admin.generalCardsCopies}
            <input
              type="number"
              min={1}
              max={200}
              value={genericCopies}
              onChange={(e) => setGenericCopies(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
              style={{ marginInlineStart: 8, width: 72 }}
            />
          </label>
          <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={() => downloadGeneric(true)}>
            {t.admin.previewGeneralCard}
          </button>
          <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={() => downloadGeneric(false)}>
            {t.admin.downloadGeneralCards}
          </button>
        </div>
      </div>

      <div className="dn-table-toolbar">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            pager.setPage(1);
          }}
          placeholder={t.admin.searchRegistrations}
          aria-label={t.admin.searchRegistrations}
          style={{ flex: "1 1 16rem", minWidth: "12rem" }}
        />
        <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={printSelectedCards}>
          {t.admin.printSelected}
        </button>
      </div>
      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Country</th>
              <th>Institution</th>
              <th>Code</th>
              <th>{t.admin.cardType}</th>
              <th>Registered</th>
              <th>Checked-in</th>
              <th className="dn-col-actions">{t.admin.actions}</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((r) => (
              <tr key={r.id}>
                <td data-label="">
                  <input
                    type="checkbox"
                    checked={!!selected[r.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                  />
                </td>
                <td data-label="Name">
                  {r.full_name}
                  {r.is_reviewer ? (
                    <>
                      {" "}
                      <span className="dn-badge-reviewer">{t.admin.reviewerBadge}</span>
                    </>
                  ) : null}
                </td>
                <td data-label="Email">{r.email}</td>
                <td data-label="Phone">{r.phone}</td>
                <td data-label="Country">{r.country}</td>
                <td data-label="Institution">{r.institution}</td>
                <td data-label="Code">{r.registration_code}</td>
                <td data-label={t.admin.cardType}>{cardTypeLabel(r, t)}</td>
                <td data-label="Registered">{formatDateTime(r.created_at, locale)}</td>
                <td data-label="Checked-in">{r.checked_in ? "✓" : "—"}</td>
                <td className="dn-col-actions" data-label={t.admin.actions}>
                  <RowActions row={r} onReload={reload} onPreview={showPreview} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar
        page={pager.page}
        pages={pager.pages}
        from={pager.from}
        to={pager.to}
        total={pager.total}
        pageSize={pager.pageSize}
        onPage={pager.setPage}
        onPageSize={pager.setPageSize}
      />
    </div>
  );
}
